// @flow
import React, { useContext, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Button,
  CurrencyInput,
  Select,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { RenewalOfferContext } from '../../context';
import messages from '../../messages';
import {
  TRANSACTION_TYPE_OPTIONS,
  mapTransactionCodesToOptions,
} from '../helpers';

type Props = {
  field: string,
  index: number,
  deleteTransaction: Function,
};

const TransactionRow = ({ field, index, deleteTransaction }: Props) => {
  const { control, setValue } = useFormContext();
  const { propertyTransactionCodes } = useContext(RenewalOfferContext);
  const [isTxType, setIsTxType] = useState(false);
  const [isTxCode, setIsTxCode] = useState(false);
  const [filteredTxCodes, setFilteredTxCodes] = useState([
    { value: 'default', text: 'Choose', disabled: true },
  ]);

  const offer1Rent = useWatch({ control, name: 'offer1Rent' });
  const offer1TermId = useWatch({ control, name: 'offer1TermId' });
  const offer2Rent = useWatch({ control, name: 'offer2Rent' });
  const offer2TermId = useWatch({ control, name: 'offer2TermId' });

  return (
    <Stack
      flexDirection="row"
      alignItems="flex-start"
      gap={1}
      data-test="transaction-row"
    >
      <Box flex={1}>
        <Typography
          variant="labelLarge"
          component="label"
          sx={{ whiteSpace: 'nowrap' }}
        >
          <FormattedMessage {...messages.type} />
        </Typography>
        <Controller
          name={`${field}.transactionType`}
          control={control}
          render={({ field: f }) => (
            <Select
              {...f}
              options={TRANSACTION_TYPE_OPTIONS}
              onChange={(e) => {
                f.onChange(e);
                const val = e.target.value;
                if (val === 'default') {
                  setFilteredTxCodes([
                    { value: 'default', text: 'Choose', disabled: true },
                  ]);
                  setIsTxType(false);
                  setIsTxCode(false);
                } else {
                  setFilteredTxCodes(
                    mapTransactionCodesToOptions(
                      propertyTransactionCodes.filter(
                        (code) =>
                          code.transactionCode.transactionType.name === val,
                      ),
                    ),
                  );
                  setValue(`${field}.id`, 'default');
                  setIsTxType(true);
                  setIsTxCode(false);
                }
              }}
              fullWidth
            />
          )}
        />
      </Box>
      <Box flex={1}>
        <Typography
          variant="labelLarge"
          component="label"
          sx={{ whiteSpace: 'nowrap' }}
        >
          <FormattedMessage {...messages.transactionCode} />
        </Typography>
        <Controller
          name={`${field}.id`}
          control={control}
          render={({ field: f, fieldState }) => (
            <Select
              {...f}
              options={filteredTxCodes}
              disabled={!isTxType}
              onChange={(e) => {
                f.onChange(e);
                setIsTxCode(true);
              }}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
      </Box>
      <Box flex={1}>
        <Typography variant="labelLarge" component="div">
          &nbsp;
        </Typography>
        <Controller
          name={`${field}.offer1`}
          control={control}
          render={({ field: f, fieldState }) => (
            <CurrencyInput
              {...f}
              disabled={!isTxCode}
              placeholder="0.00"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
      </Box>
      <Box flex={1}>
        <Typography variant="labelLarge" component="div">
          &nbsp;
        </Typography>
        <Controller
          name={`${field}.offer2`}
          control={control}
          render={({ field: f, fieldState }) => (
            <CurrencyInput
              {...f}
              disabled={
                !offer1Rent || !offer1TermId || +offer1Rent <= 0 || !isTxCode
              }
              placeholder="0.00"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
      </Box>
      <Box flex={1}>
        <Typography variant="labelLarge" component="div">
          &nbsp;
        </Typography>
        <Controller
          name={`${field}.offer3`}
          control={control}
          render={({ field: f, fieldState }) => (
            <CurrencyInput
              {...f}
              disabled={
                !offer2Rent || !offer2TermId || +offer2Rent <= 0 || !isTxCode
              }
              placeholder="0.00"
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              fullWidth
            />
          )}
        />
      </Box>
      <Box sx={{ width: 40, flexShrink: 0 }}>
        <Typography variant="labelLarge" component="div">
          &nbsp;
        </Typography>
        <Button
          variant="text"
          onClick={() => deleteTransaction(index)}
          aria-label="Remove transaction"
        >
          <i className="et-trash" />
        </Button>
      </Box>
    </Stack>
  );
};

export default TransactionRow;
